import type { ReactNode } from "react";
import { ArrowLeft, BookOpenCheck, CalendarDays, CircleDollarSign, Landmark, LayoutDashboard, Menu, RefreshCw, Settings2, UsersRound, WalletCards, X } from "lucide-react";
import { MemberPhoto } from "./MemberPhoto";
import { bniChapterName } from "../chapter-name";

export type Page = "home" | "getting-started" | "speakers" | "renewals" | "dues" | "checking" | "member-ach" | "chapter-success" | "settings";
const items: { id: Page; label: string; icon: typeof LayoutDashboard }[] = [
  { id: "home", label: "Home", icon: LayoutDashboard },
  { id: "getting-started", label: "Getting Started & FAQs", icon: BookOpenCheck },
  { id: "speakers", label: "Meeting Prep", icon: CalendarDays },
  { id: "renewals", label: "Member Renewals", icon: RefreshCw },
  { id: "checking", label: "Money", icon: Landmark },
  { id: "member-ach", label: "Collect Chapter Dues", icon: WalletCards },
  { id: "chapter-success", label: "Chapter Success Payment", icon: CircleDollarSign },
  { id: "settings", label: "Settings & Help", icon: Settings2 }
];

interface Props {
  page: Page;
  setPage: (page: Page) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  chapterName: string;
  regionalView?: boolean;
  speakerOnlyView?: boolean;
  incomingReadOnly?: boolean;
  displayName?: string;
  profilePhotoUrl?: string;
  auditChapters?: { id: string; name: string }[];
  onAuditChapterChange?: (chapterId: string) => void;
  auditChapterChanging?: boolean;
  children: ReactNode;
}

export function AppShell({ page, setPage, open, setOpen, chapterName, regionalView = false, speakerOnlyView = false, incomingReadOnly = false, displayName, profilePhotoUrl, auditChapters = [], onAuditChapterChange, auditChapterChanging = false, children }: Props) {
  const navigate = (next: Page) => { setPage(next); setOpen(false); };
  const brandedChapterName = bniChapterName(chapterName);
  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`} aria-label="Primary navigation">
        <div className="brand">
          <a className="brand-mark brand-portal-link" href="https://portal.bnitampa.com" aria-label="Return to BNI Tampa Bay Portal">BNI</a>
          <div><strong>Tampa Bay</strong><span>{speakerOnlyView ? "Speaker Operations" : incomingReadOnly ? "Incoming Secretary / Treasurer" : "Secretary / Treasurer"}</span></div>
          <button className="icon-button sidebar-close" onClick={() => setOpen(false)} aria-label="Close menu"><X size={20} /></button>
        </div>
        <div className="chapter-chip"><UsersRound size={17} /><span><small>{speakerOnlyView ? "Selected Chapter" : "Your Chapter"}</small><strong>{brandedChapterName}</strong></span></div>
        {auditChapters.length && onAuditChapterChange ? <label className="chapter-audit-selector"><span>Audit as Secretary/Treasurer</span><select aria-label="Choose a Chapter to audit" value={auditChapters.find((chapter) => bniChapterName(chapter.name) === brandedChapterName)?.id || ""} disabled={auditChapterChanging} onChange={(event) => event.target.value && onAuditChapterChange(event.target.value)}><option value="" disabled>Choose a Chapter</option>{auditChapters.map((chapter) => <option key={chapter.id} value={chapter.id}>{bniChapterName(chapter.name)}</option>)}</select><small>{auditChapterChanging ? "Opening Chapter…" : "Read-only · Visitation sync audit"}</small></label> : null}
        {regionalView ? <a className="region-return" href={speakerOnlyView ? "/speaker-region" : "/region"}><ArrowLeft size={16} />Return to {speakerOnlyView ? "Speaker Operations" : "Region Dashboard"}</a> : null}
        <nav>
          {items.filter((item) => incomingReadOnly ? item.id === "home" : !speakerOnlyView || item.id === "speakers").map(({ id, label, icon: Icon }) => (
            <button key={id} className={page === id ? "nav-active" : ""} onClick={() => navigate(id)}>
              <Icon size={19} /><span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-help"><span>Need help?</span><a href="https://www.bnitampa.com" target="_blank" rel="noreferrer">Open the Leadership Resource Guide →</a></div>
      </aside>
      {open ? <button className="scrim" onClick={() => setOpen(false)} aria-label="Close navigation" /> : null}
      <main>
        <header className="topbar">
          <button className="icon-button menu-button" onClick={() => setOpen(true)} aria-label="Open menu"><Menu /></button>
          <a className="mobile-brand" href="https://portal.bnitampa.com" aria-label="Return to BNI Tampa Bay Portal"><strong>BNI Tampa Bay</strong><span>{speakerOnlyView ? "Speaker Operations" : incomingReadOnly ? "Incoming Secretary / Treasurer" : "Secretary / Treasurer"}</span></a>
          <div className="profile"><MemberPhoto member={{ fullName: regionalView ? speakerOnlyView ? "Speaker Operations" : "Region Team" : displayName || "Secretary / Treasurer", photoUrl: regionalView ? undefined : profilePhotoUrl }} className="avatar" eager /><span><strong>{regionalView ? speakerOnlyView ? "Speaker Rotation operator" : "Regional read-only view" : displayName || "Secretary / Treasurer"}</strong><small>{brandedChapterName}</small></span></div>
        </header>
        <div className="content">{children}</div>
      </main>
    </div>
  );
}
